class TkPriceRange extends HTMLElement {
  connectedCallback() {
    this._rangeMax = Number(this.dataset.rangeMax) || 0;
    this._step = Number(this.dataset.step) || 100;

    if (this._rangeMax === 0) {
      this.hidden = true;
      return;
    }

    const ranges = this.querySelectorAll('.tk-price-range__range');
    const numbers = this.querySelectorAll('.tk-price-range__number');
    this._minRange = ranges[0];
    this._maxRange = ranges[1];
    this._minNumber = numbers[0];
    this._maxNumber = numbers[1];

    if (!this._minRange || !this._maxRange) return;

    this._eurFormatter = new Intl.NumberFormat('it-IT', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    this._isPristine = true;
    this._restoreFromURL();
    this._syncNumberFromRange('min');
    this._syncNumberFromRange('max');
    this._updateAriaValueText('min');
    this._updateAriaValueText('max');
    this._updateFill();

    this._onMinRangeInput = () => this._handleRangeInput('min');
    this._onMaxRangeInput = () => this._handleRangeInput('max');
    this._onMinRangeChange = (e) => this._handleRangeChange(e, 'min');
    this._onMaxRangeChange = (e) => this._handleRangeChange(e, 'max');
    this._onMinNumberInput = () => this._handleNumberInput('min');
    this._onMaxNumberInput = () => this._handleNumberInput('max');
    this._onMinNumberBlur = (e) => this._handleNumberBlur(e, 'min');
    this._onMaxNumberBlur = (e) => this._handleNumberBlur(e, 'max');
    this._onMinNumberKey = (e) => this._handleNumberKey(e);
    this._onMaxNumberKey = (e) => this._handleNumberKey(e);

    this._minRange.addEventListener('input', this._onMinRangeInput);
    this._maxRange.addEventListener('input', this._onMaxRangeInput);
    this._minRange.addEventListener('change', this._onMinRangeChange);
    this._maxRange.addEventListener('change', this._onMaxRangeChange);
    this._minNumber.addEventListener('input', this._onMinNumberInput);
    this._maxNumber.addEventListener('input', this._onMaxNumberInput);
    this._minNumber.addEventListener('blur', this._onMinNumberBlur);
    this._maxNumber.addEventListener('blur', this._onMaxNumberBlur);
    this._minNumber.addEventListener('keydown', this._onMinNumberKey);
    this._maxNumber.addEventListener('keydown', this._onMaxNumberKey);
  }

  disconnectedCallback() {
    if (!this._minRange) return;
    this._minRange.removeEventListener('input', this._onMinRangeInput);
    this._maxRange.removeEventListener('input', this._onMaxRangeInput);
    this._minRange.removeEventListener('change', this._onMinRangeChange);
    this._maxRange.removeEventListener('change', this._onMaxRangeChange);
    this._minNumber.removeEventListener('input', this._onMinNumberInput);
    this._maxNumber.removeEventListener('input', this._onMaxNumberInput);
    this._minNumber.removeEventListener('blur', this._onMinNumberBlur);
    this._maxNumber.removeEventListener('blur', this._onMaxNumberBlur);
    this._minNumber.removeEventListener('keydown', this._onMinNumberKey);
    this._maxNumber.removeEventListener('keydown', this._onMaxNumberKey);
  }

  _restoreFromURL() {
    const params = new URLSearchParams(window.location.search);
    const gte = params.get(this._minNumber.name);
    const lte = params.get(this._maxNumber.name);
    if (gte !== null && gte !== '') {
      const cents = this._clamp('min', this._parseLocale(gte));
      this._minRange.value = String(cents);
      this._isPristine = false;
    }
    if (lte !== null && lte !== '') {
      const cents = this._clamp('max', this._parseLocale(lte));
      this._maxRange.value = String(cents);
      this._isPristine = false;
    }
  }

  _parseLocale(str) {
    if (typeof str !== 'string') {
      const n = Number(str);
      return Number.isFinite(n) ? Math.round(n * 100) : 0;
    }
    const trimmed = str.trim();
    if (trimmed === '') return 0;
    const hasComma = trimmed.includes(',');
    const hasDot = trimmed.includes('.');
    let normalized;
    if (hasComma && hasDot) {
      normalized = trimmed.replace(/\./g, '').replace(',', '.');
    } else if (hasComma) {
      normalized = trimmed.replace(',', '.');
    } else {
      normalized = trimmed;
    }
    const n = parseFloat(normalized);
    return Number.isFinite(n) ? Math.round(n * 100) : 0;
  }

  _clamp(side, rawCents) {
    const cents = Math.round(Number(rawCents) || 0);
    if (side === 'min') {
      const maxCents = Number(this._maxRange.value);
      return Math.max(0, Math.min(cents, maxCents - this._step));
    }
    const minCents = Number(this._minRange.value);
    return Math.min(this._rangeMax, Math.max(cents, minCents + this._step));
  }

  _handleRangeInput(side) {
    this._isPristine = false;
    const range = side === 'min' ? this._minRange : this._maxRange;
    const clamped = this._clamp(side, range.value);
    if (Number(range.value) !== clamped) range.value = String(clamped);
    this._syncNumberFromRange(side);
    this._updateAriaValueText(side);
    this._updateFill();
    this._updateZIndex(side);
  }

  _handleRangeChange(event) {
    const side = event.target.dataset.side;
    const number = side === 'min' ? this._minNumber : this._maxNumber;
    this._commit(number);
  }

  _handleNumberInput(side) {
    this._isPristine = false;
    const number = side === 'min' ? this._minNumber : this._maxNumber;
    const range = side === 'min' ? this._minRange : this._maxRange;
    const raw = number.value;
    if (raw === '') {
      range.value = side === 'min' ? '0' : String(this._rangeMax);
    } else {
      const cents = this._parseLocale(raw);
      const clamped = this._clamp(side, cents);
      range.value = String(clamped);
    }
    this._updateAriaValueText(side);
    this._updateFill();
  }

  _handleNumberBlur(event, side) {
    const number = side === 'min' ? this._minNumber : this._maxNumber;
    if (number.value !== '') {
      const cents = this._clamp(side, this._parseLocale(number.value));
      number.value = this._formatCents(cents);
    }
    this._commit(number);
  }

  _handleNumberKey(event) {
    const allowed = /[0-9]|\.|,|Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Home|End|Escape/;
    if (event.metaKey || event.ctrlKey) return;
    if (!allowed.test(event.key) && event.key.length === 1) event.preventDefault();
    if (event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.blur();
    }
  }

  _syncNumberFromRange(side) {
    const range = side === 'min' ? this._minRange : this._maxRange;
    const number = side === 'min' ? this._minNumber : this._maxNumber;
    if (document.activeElement === number) return;
    number.value = this._formatCents(Number(range.value));
  }

  _formatCents(cents) {
    if (!Number.isFinite(cents)) return '';
    return this._eurFormatter.format(cents / 100);
  }

  _updateAriaValueText(side) {
    const range = side === 'min' ? this._minRange : this._maxRange;
    range.setAttribute('aria-valuetext', `${this._formatCents(Number(range.value))} €`);
  }

  _updateFill() {
    if (this._rangeMax === 0) return;
    const minPct = (Number(this._minRange.value) / this._rangeMax) * 100;
    const maxPct = (Number(this._maxRange.value) / this._rangeMax) * 100;
    this.style.setProperty('--tk-price-range-fill-start', `${minPct}%`);
    this.style.setProperty('--tk-price-range-fill-end', `${maxPct}%`);
  }

  _updateZIndex(side) {
    if (side === 'min') {
      this._minRange.style.zIndex = '2';
      this._maxRange.style.zIndex = '1';
    } else {
      this._minRange.style.zIndex = '1';
      this._maxRange.style.zIndex = '2';
    }
  }

  _commit(sourceEl) {
    sourceEl.dispatchEvent(new Event('input', { bubbles: true }));
  }
}

customElements.define('tk-price-range', TkPriceRange);
